import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Empty,
    Form,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Tag,
    Typography,
} from "antd";
import type { ScreenMap } from "antd/es/_util/responsiveObserver";
import { BracketView, EntrantStatusList, MatchCard, SlotConflictPrompt, StandingsTable, TournamentHeader, findSlotConflict } from "@dowty/tournament-ui";
import type { Match as UiMatch, SlotConflict, Tournament as UiTournament } from "@dowty/tournament-ui";
import "@dowty/tournament-ui/styles.css";
import dayjs from "dayjs";
import type { Tournament, TournamentMatch, TournamentTeam } from "../../../services/beerpongService";
import { TournamentStatus, TournamentStyle, BracketType, TournamentVisibility } from "../../../generated/bpong_pb";
import {
    isDoubleEliminationStyle,
    isEliminationStyle,
    styleLabel,
    buildStyleOptions,
    buildVisibilityOptions,
} from "./constants";
import { useTranslation } from "react-i18next";
import { toTournamentUi } from "./tournamentAdapter";
import { StatusTag } from "@dowty/ui";

const { Text, Paragraph } = Typography;

type TournamentDetailsProps = {
    tournament: Tournament | null;
    updatingMatchId: number | null;
    updatingTournamentId: number | null;
    unlockingTournamentId: number | null;
    eliminatingTeamId: number | null;
    advancingTournamentId: number | null;
    restoringTeamId: number | null;
    onMatchWinnerChange: (
        tournament: Tournament,
        match: TournamentMatch,
        payload: { winnerTeamId: number | null }
    ) => void | Promise<void>;
    onMatchTeamsChange: (
        tournament: Tournament,
        match: TournamentMatch,
        payload: { teamAId?: number; teamBId?: number }
    ) => void | Promise<void>;
    onTournamentStatusChange: (tournament: Tournament, status: TournamentStatus) => void;
    onTournamentStyleChange: (tournament: Tournament, style: TournamentStyle) => void;
    onTournamentVisibilityChange: (
        tournament: Tournament,
        visibility: TournamentVisibility,
        password?: string | null
    ) => Promise<boolean>;
    onEliminateTeam: (tournament: Tournament, team: TournamentTeam) => void;
    onRestoreTeam: (tournament: Tournament, team: TournamentTeam) => void;
    onAdvanceTournament: (tournament: Tournament) => void;
    onUnlockTournament: (tournament: Tournament, password: string) => void;
    onDeleteTournament: (tournament: Tournament) => void;
    deletingTournamentId: number | null;
    screens: ScreenMap;
    currentUserId: number | null;
};

type SlotConflictState = { conflict: SlotConflict; match: TournamentMatch; slot: "A" | "B"; entrantId: number };

type RoundRobinLeader = {
    teamId: number;
    teamName: string;
    wins: number;
    losses: number;
};

type RoundRobinOutcome = {
    leaders: RoundRobinLeader[];
    pendingMatches: number;
};

const updateTeamStatus = (
    tournament: Tournament,
    teamId: number,
    update: TournamentDetailsProps["onEliminateTeam"]
) => {
    const team = tournament.teams.find((item) => item.teamId === teamId);
    if (team) update(tournament, team);
};

const updateSwappedTeams = async (
    tournament: Tournament,
    slotConflict: SlotConflictState,
    conflict: SlotConflict,
    onMatchTeamsChange: TournamentDetailsProps["onMatchTeamsChange"],
    setSlotConflict: React.Dispatch<React.SetStateAction<SlotConflictState | null>>
) => {
    const other = tournament.matches.find((candidate) => candidate.matchId === conflict.otherMatchId);
    if (!other) return setSlotConflict(null);
    const update = (target: TournamentMatch, targetSlot: "A" | "B", teamId: number | null) => onMatchTeamsChange(tournament, target, targetSlot === "A" ? { teamAId: teamId ?? 0 } : { teamBId: teamId ?? 0 });
    await update(slotConflict.match, slotConflict.slot, slotConflict.entrantId);
    await update(other, conflict.otherSlot, conflict.displacedEntrantId);
    setSlotConflict(null);
};

const getTournamentDisplayStatus = (status: TournamentStatus): "Cancelled" | "Completed" | "Running" => {
    if (status === TournamentStatus.TOURNAMENT_STATUS_CANCELLED) return "Cancelled";
    if (status === TournamentStatus.TOURNAMENT_STATUS_COMPLETED) return "Completed";
    return "Running";
};

const isLockedTournamentView = (tournament: Tournament) =>
    tournament.requiresPassword && !tournament.isOwner && tournament.teams.length === 0 && tournament.matches.length === 0;

const canManageTournament = (tournament: Tournament, currentUserId: number | null) =>
    tournament.isOwner || (currentUserId !== null && tournament.createdBy === currentUserId);

const getRoundRobinWinnerIds = (outcome: RoundRobinOutcome | null) =>
    outcome?.pendingMatches === 0 ? outcome.leaders.map((leader) => leader.teamId) : [];

const getCompletedWinner = (
    outcome: RoundRobinOutcome | null,
    tournament: Tournament,
    completedMatch: TournamentMatch | undefined
) => outcome?.pendingMatches === 0
    ? outcome.leaders.map((leader) => leader.teamName).join(", ")
    : tournament.teams.find((team) => team.teamId === completedMatch?.winnerTeamId)?.teamName ?? "";

const getCompletedMatch = (tournament: Tournament) => [...tournament.matches]
    .filter((match) => !match.isCancelled && match.winnerTeamId)
    .sort((a, b) => Number(a.bracketType === BracketType.BRACKET_TYPE_FINALS) - Number(b.bracketType === BracketType.BRACKET_TYPE_FINALS) || (a.roundNumber ?? 0) - (b.roundNumber ?? 0) || a.matchId - b.matchId)
    .at(-1);

const countRoundRobinMatch = (
    match: TournamentMatch,
    stats: Map<number, RoundRobinLeader>
): { considered: number; resolved: number } => {
    if (match.isCancelled) return { considered: 0, resolved: 0 };
    if (!match.winnerTeamId) return { considered: 1, resolved: 0 };

    const winnerStats = stats.get(match.winnerTeamId);
    if (winnerStats) winnerStats.wins += 1;
    let opponentId: number | null = null;
    if (match.teamAId !== match.teamBId) {
        opponentId = match.winnerTeamId === match.teamAId ? match.teamBId : match.teamAId;
    }
    if (opponentId && stats.has(opponentId)) stats.get(opponentId)!.losses += 1;
    return { considered: 1, resolved: 1 };
};

const buildRoundRobinOutcome = (tournament: Tournament | null): RoundRobinOutcome | null => {
    if (
        tournament?.style !== TournamentStyle.TOURNAMENT_STYLE_ROUND_ROBIN ||
        tournament.teams.length === 0
    ) return null;

    const stats = new Map<number, RoundRobinLeader>();
    tournament.teams.forEach((team) => stats.set(team.teamId, {
        teamId: team.teamId,
        teamName: team.teamName,
        wins: 0,
        losses: 0,
    }));
    let consideredMatches = 0;
    let resolvedMatches = 0;
    tournament.matches.forEach((match) => {
        const count = countRoundRobinMatch(match, stats);
        consideredMatches += count.considered;
        resolvedMatches += count.resolved;
    });
    if (resolvedMatches === 0) return null;

    const maxWins = Math.max(...Array.from(stats.values()).map((entry) => entry.wins));
    if (!Number.isFinite(maxWins) || maxWins <= 0) return null;
    const leaders = Array.from(stats.values()).filter((entry) => entry.wins === maxWins);
    if (leaders.length === 0) return null;
    return { leaders, pendingMatches: Math.max(consideredMatches - resolvedMatches, 0) };
};

const hasUnresolvedMatchesInLatestRound = (matches: TournamentMatch[]): boolean => {
    if (!matches.length) return false;
    const latestRound = Math.max(...matches.map((match) => match.roundNumber ?? 1));
    return matches.some(
        (match) => (match.roundNumber ?? 1) === latestRound && !match.winnerTeamId && match.teamAId !== match.teamBId
    );
};

const canAdvanceTournament = (tournament: Tournament): boolean => {
    if (
        !isEliminationStyle(tournament.style) ||
        tournament.status !== TournamentStatus.TOURNAMENT_STATUS_ACTIVE ||
        tournament.teams.filter((team) => !team.isEliminated).length <= 1
    ) return false;

    const winnerMatches = tournament.matches.filter(
        (match) =>
            (match.bracketType === BracketType.BRACKET_TYPE_WINNERS ||
                match.bracketType === BracketType.BRACKET_TYPE_UNSPECIFIED) &&
            !match.isCancelled
    );
    if (!winnerMatches.length || hasUnresolvedMatchesInLatestRound(winnerMatches)) return false;

    const loserMatches = tournament.matches.filter(
        (match) => match.bracketType === BracketType.BRACKET_TYPE_LOSERS && !match.isCancelled
    );
    if (isDoubleEliminationStyle(tournament.style) && hasUnresolvedMatchesInLatestRound(loserMatches)) return false;

    return !tournament.matches.some(
        (match) =>
            match.bracketType === BracketType.BRACKET_TYPE_FINALS &&
            !match.isCancelled &&
            !match.winnerTeamId
    );
};

type MatchRenderContext = Pick<TournamentDetailsProps, "onMatchWinnerChange" | "onMatchTeamsChange"> & {
    tournament: Tournament; updatingMatchId: number | null; uiTournament: UiTournament | null;
    eliminationEnabled: boolean; t: ReturnType<typeof useTranslation>["t"];
    setSlotConflict: React.Dispatch<React.SetStateAction<SlotConflictState | null>>;
};
const renderMatchContent = (context: MatchRenderContext, match: TournamentMatch, matchNumberMap: Map<number, number>, canEditMatches: boolean) => {
    const { t, tournament, eliminationEnabled, uiTournament, onMatchTeamsChange, onMatchWinnerChange, updatingMatchId, setSlotConflict } = context;
    const teamMap = new Map(tournament.teams.map((team) => [team.teamId, team]));
    const teamA = teamMap.get(match.teamAId);
    const teamB = teamMap.get(match.teamBId);

    const eligibleTeams = tournament.teams.filter((team) =>
        !eliminationEnabled || !team.isEliminated || team.teamId === teamA?.teamId || team.teamId === teamB?.teamId
    );

    const seenTeamIds = new Set<number>();
    const teamOptions = eligibleTeams
        .filter((team) => {
            if (seenTeamIds.has(team.teamId)) {
                return false;
            }
            seenTeamIds.add(team.teamId);
            return true;
        })
        .map((team) => ({
            label: team.teamName,
            value: team.teamId,
        }));

    const participants = [teamA, teamB].filter(
        (team, index, array) =>
            team?.teamId !== undefined && array.findIndex((candidate) => candidate?.teamId === team.teamId) === index
    ) as TournamentTeam[];

    const winnerOptions = [
        { label: t("beerpong.tournament.details.match.pending"), value: 0 },
        ...participants.map((team) => ({
            label: team.teamName,
            value: team.teamId,
        })),
    ];

    const winnerSelectDisabled =
        !canEditMatches || match.isCancelled || winnerOptions.length <= 1;
    const canEditTeams = canEditMatches && !match.isCancelled && !match.winnerTeamId;
    const currentWinner = match.winnerTeamId ?? 0;
    const matchNumber = matchNumberMap.get(match.matchId) ?? match.matchId;

    const handleTeamChange = (slot: "A" | "B", value: number) => {
        if (Number.isNaN(value) || (slot === "A" ? teamA?.teamId : teamB?.teamId) === value) return;
        const conflict = uiTournament && findSlotConflict({ matches: uiTournament.matches.filter((candidate) => candidate.tableLabel !== "Cancelled"), matchId: match.matchId, slot, newEntrantId: value });
        if (conflict) {
            setSlotConflict({ conflict, match, slot, entrantId: value });
            return;
        }
        onMatchTeamsChange(tournament, match, slot === "A" ? { teamAId: value } : { teamBId: value });
    };

    const renderTeamLine = (slot: "A" | "B", team: TournamentTeam | undefined) => {
        const label = t("beerpong.tournament.details.match.teamSlot", { slot });
        const isWinner = match.winnerTeamId === team?.teamId && !match.isCancelled;

        return (
            <Space size="small" align="center" wrap>
                <Text>
                    <strong>{label}:</strong> {team?.teamName ?? t("beerpong.tournament.details.match.tbd")}
                </Text>
                {canEditTeams && (
                    <Select<number>
                        style={{ minWidth: 160 }}
                        value={team?.teamId}
                        options={teamOptions}
                        placeholder={t("beerpong.tournament.details.match.selectTeam")}
                        onChange={(value) => handleTeamChange(slot, Number(value))}
                        loading={updatingMatchId === match.matchId}
                    />
                )}
                {isWinner && <StatusTag tone="success">{t("beerpong.tournament.details.match.winnerTag")}</StatusTag>}
                {eliminationEnabled && team?.isEliminated && (
                    <StatusTag tone="error">{t("beerpong.tournament.details.match.eliminatedTag")}</StatusTag>
                )}
            </Space>
        );
    };

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">
                {t("beerpong.tournament.details.match.label", { number: matchNumber })}
            </Text>
            {renderTeamLine("A", teamA)}
            {renderTeamLine("B", teamB)}
            <Space align="center" style={{ width: "100%", gap: 8 }}>
                <Text style={{ width: 72, minWidth: 72 }}>
                    {t("beerpong.tournament.details.match.winnerLabel")}
                </Text>
                <Select<number>
                    value={currentWinner}
                    onChange={(value) =>
                        onMatchWinnerChange(tournament, match, {
                            winnerTeamId: Number(value) === 0 ? null : Number(value),
                        })
                    }
                    options={winnerOptions}
                    style={{ flex: 1, minWidth: 140 }}
                    disabled={winnerSelectDisabled}
                    loading={updatingMatchId === match.matchId}
                />
            </Space>
            {match.isCancelled && <StatusTag tone="error">{t("beerpong.tournament.details.match.cancelledTag")}</StatusTag>}
            {teamA?.teamId !== undefined && teamA.teamId === teamB?.teamId && !match.isCancelled && (
                <Text type="secondary">
                    {t("beerpong.tournament.details.match.bye", { team: teamA.teamName })}
                </Text>
            )}
        </Space>
    );
};

const TournamentDetails: React.FC<TournamentDetailsProps> = ({
    tournament,
    updatingMatchId,
    updatingTournamentId,
    unlockingTournamentId,
    eliminatingTeamId,
    advancingTournamentId,
    restoringTeamId,
    onMatchWinnerChange,
    onMatchTeamsChange,
    onTournamentStatusChange,
    onTournamentStyleChange,
    onTournamentVisibilityChange,
    onEliminateTeam,
    onRestoreTeam,
    onAdvanceTournament,
    onUnlockTournament,
    onDeleteTournament,
    deletingTournamentId,
    screens,
    currentUserId,
}) => {
    const [accessModalVisible, setAccessModalVisible] = useState<boolean>(false);
    const [accessForm] = Form.useForm<{ visibility: TournamentVisibility; password?: string }>();
    const [unlockForm] = Form.useForm<{ password: string }>();
    const watchedVisibility = Form.useWatch(
        "visibility",
        accessForm
    ) as TournamentVisibility | undefined;
    const [updatingAccess, setUpdatingAccess] = useState<boolean>(false);
    const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
    const [slotConflict, setSlotConflict] = useState<SlotConflictState | null>(null);
    const { t } = useTranslation();
    const conflictLabels = {
        alsoPlays: (team: string, match: string) => t("beerpong.tournament.details.conflict.alsoPlays", { team, match }),
        swap: t("beerpong.tournament.details.conflict.swap"),
        putsThere: (team: string) => t("beerpong.tournament.details.conflict.putsThere", { team }),
        keepBoth: t("beerpong.tournament.details.conflict.keepBoth"),
        cancel: t("beerpong.tournament.details.conflict.cancel"),
        emptySlot: t("beerpong.tournament.details.conflict.emptySlot"),
    };
    const styleOptions = useMemo(() => buildStyleOptions(t), [t]);
    const visibilityOptions = useMemo(() => buildVisibilityOptions(t), [t]);

    const roundRobinOutcome = useMemo(() => buildRoundRobinOutcome(tournament), [tournament]);
    const uiTournament = useMemo(() => tournament ? toTournamentUi(tournament) : null, [tournament]);

    if (!tournament) {
        return (
            <Card style={{ background: "transparent" }}>
                <Empty description={t("beerpong.tournament.details.empty")} />
            </Card>
        );
    }

    const eliminationEnabled = isEliminationStyle(tournament.style);

    const renderTeamParticipants = (team: TournamentTeam) => team.members.length
        ? team.members.map((member) => member.displayName).join(", ")
        : t("beerpong.tournament.details.noParticipants");

    const renderRoundRobinMatches = (uiTournament: UiTournament, canEditMatches: boolean) => {
        const matches = [...uiTournament.matches].sort((a, b) => a.round - b.round || a.position - b.position);
        if (matches.length === 0) return <Paragraph type="secondary">{t("beerpong.tournament.details.matches.empty")}</Paragraph>;
        return (
            <div className="bp-round-robin-matches">
                {matches.map((match) => (
                    <MatchCard
                        key={match.id}
                        match={match}
                        entrants={uiTournament.entrants}
                        labels={{ pass: t("beerpong.tournament.details.match.pass"), passHint: t("beerpong.tournament.details.match.passHint") }}
                        onSelect={() => setSelectedMatchId(match.id)}
                        readOnly={!canEditMatches}
                    />
                ))}
            </div>
        );
    };
    const canAdvance = canAdvanceTournament(tournament);

    const visibilityMeta =
        visibilityOptions.find((option) => option.value === tournament.visibility) ??
        visibilityOptions[0];

    const openAccessModal = () => {
        accessForm.setFieldsValue({
            visibility: tournament.visibility,
            password: "",
        });
        setAccessModalVisible(true);
    };

    const closeAccessModal = () => {
        setAccessModalVisible(false);
        accessForm.resetFields();
    };

    const handleAccessSubmit = async () => {
        try {
            const values = await accessForm.validateFields();
            setUpdatingAccess(true);
            const password =
                values.visibility === TournamentVisibility.TOURNAMENT_VISIBILITY_PASSWORD
                    ? values.password ?? ""
                    : null;
            if (await onTournamentVisibilityChange(tournament, values.visibility, password) !== false) closeAccessModal();
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            console.error("Failed to update tournament access", error);
        } finally {
            setUpdatingAccess(false);
        }
    };

    const modalVisibility =
        watchedVisibility ?? tournament.visibility;
    const modalRequiresPassword =
        modalVisibility === TournamentVisibility.TOURNAMENT_VISIBILITY_PASSWORD;

    const canManage = canManageTournament(tournament, currentUserId);
    const isActive = tournament.status === TournamentStatus.TOURNAMENT_STATUS_ACTIVE;
    const canEditMatches = canManage && isActive;
    const canManageElimination = canManage && isActive && eliminationEnabled;
    const matchRenderContext: MatchRenderContext = { t, tournament, eliminationEnabled, uiTournament, onMatchWinnerChange, onMatchTeamsChange, updatingMatchId, setSlotConflict };
    const isDeletingTournament =
        deletingTournamentId !== null && tournament.tournamentId === deletingTournamentId;
    const tournamentDisplayStatus = getTournamentDisplayStatus(tournament.status);

    const sortedMatches = [...tournament.matches].sort((a, b) => a.matchId - b.matchId);
    const matchNumberMap = new Map<number, number>();
    sortedMatches.forEach((match, index) => {
        matchNumberMap.set(match.matchId, index + 1);
    });
    const selectedMatch = tournament.matches.find((match) => match.matchId === selectedMatchId) ?? null;
    const completedMatch = getCompletedMatch(tournament);
    const completedWinner = getCompletedWinner(roundRobinOutcome, tournament, completedMatch);

    const cardPadding = 16;
    // If it's a round robin and finished, these are the winner team IDs
    const roundRobinWinnerIds = getRoundRobinWinnerIds(roundRobinOutcome);

    const isLockedView = isLockedTournamentView(tournament);

    const isUnlocking = unlockingTournamentId === tournament.tournamentId;
    const eliminationRoundByTeam = new Map<number, number>();
    tournament.matches.forEach((match) => {
        if (match.isCancelled || !match.winnerTeamId) return;
        const eliminatedId = match.winnerTeamId === match.teamAId ? match.teamBId : match.teamAId;
        eliminationRoundByTeam.set(eliminatedId, Math.max(eliminationRoundByTeam.get(eliminatedId) ?? 0, match.roundNumber ?? 1));
    });

    const handleUnlockSubmit = async ({ password }: { password: string }) => {
        try {
            await onUnlockTournament(tournament, password.trim());
            unlockForm.resetFields();
        } catch (error) {
            console.error("Failed to unlock tournament", error);
        }
    };

    const renderTeams = () => canManageElimination ? (
        <EntrantStatusList
            entrants={uiTournament?.entrants ?? []}
            showMembers
            onEliminate={(entrant) => updateTeamStatus(tournament, entrant.id, onEliminateTeam)}
            onRestore={(entrant) => updateTeamStatus(tournament, entrant.id, onRestoreTeam)}
            busyId={eliminatingTeamId ?? restoringTeamId}
            labels={{ active: t("beerpong.tournament.entrants.active"), out: t("beerpong.tournament.entrants.out"), eliminate: t("beerpong.tournament.entrants.eliminate"), restore: t("beerpong.tournament.entrants.restore"), summary: (active, out) => t("beerpong.tournament.entrants.summary", { active, out }), empty: t("beerpong.tournament.entrants.empty") }}
        />
    ) : (
        <div className="bp-team-section">
            <Text strong>{t("beerpong.tournament.details.sections.teams")}</Text>
            <div className="bp-team-chips">
                {tournament.teams.map((team) => (
                    <div className={`bp-team-chip${team.isEliminated ? " is-eliminated" : ""}`} key={team.teamId}>
                        <div className="bp-team-chip-copy">
                            <Text strong>{team.teamName}</Text>
                            <Text type="secondary">{renderTeamParticipants(team)}</Text>
                        </div>
                        {eliminationEnabled && team.isEliminated && <StatusTag tone="error">{eliminationRoundByTeam.get(team.teamId)
                            ? t("beerpong.tournament.details.match.eliminatedRound", { round: eliminationRoundByTeam.get(team.teamId) })
                            : t("beerpong.tournament.details.match.eliminatedTag")}</StatusTag>}
                        {roundRobinWinnerIds.includes(team.teamId) && <StatusTag tone="success">{t("beerpong.tournament.details.match.winnerTag")}</StatusTag>}
                        {canManageElimination && (team.isEliminated ? (
                            <Button type="text" onClick={() => onRestoreTeam(tournament, team)} loading={restoringTeamId === team.teamId}>
                                {t("beerpong.tournament.details.actions.restoreTeam")}
                            </Button>
                        ) : (
                            <Popconfirm title={t("beerpong.tournament.details.popconfirm.eliminateTitle")} onConfirm={() => onEliminateTeam(tournament, team)}>
                                <Button type="text" danger loading={eliminatingTeamId === team.teamId}>
                                    {t("beerpong.tournament.details.actions.eliminateTeam")}
                                </Button>
                            </Popconfirm>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderMatches = () => (
        <div className="bp-matches-section">
            <Text strong>{t("beerpong.tournament.details.sections.matches")}</Text>
            {uiTournament && (uiTournament.stages[0].format === "round_robin" ? (
                <>
                    <StandingsTable tournament={uiTournament} stageId={uiTournament.stages[0].id} />
                    {renderRoundRobinMatches(uiTournament, canEditMatches)}
                </>
            ) : (
                <div className="tournament-bracket-scroll">
                    <Text type="secondary" className="tournament-bracket-hint">{t("tournamentUi.bracketScrollHint")}</Text>
                    <BracketView
                        tournament={uiTournament}
                        onSelect={(match: UiMatch) => setSelectedMatchId(match.id)}
                        matchLabels={{ pass: t("tournamentUi.pass"), passHint: t("tournamentUi.passHint") }}
                    />
                </div>
            ))}
            {tournament.matches.some((match) => match.isCancelled) && (
                <div className="bp-cancelled-matches">
                    {tournament.matches.filter((match) => match.isCancelled).map((match) => (
                        <Tag key={match.matchId} color="red">
                            {t("beerpong.tournament.details.match.label", { number: matchNumberMap.get(match.matchId) ?? match.matchId })}: {t("beerpong.tournament.details.match.cancelledTag")}
                        </Tag>
                    ))}
                </div>
            )}
        </div>
    );

    const headerActions = canManage && (
        <Space wrap>
            <Button onClick={openAccessModal} disabled={updatingTournamentId === tournament.tournamentId}>
                {t("beerpong.tournament.details.actions.manageAccess")}
            </Button>
            <Select<TournamentStyle>
                value={tournament.style}
                onChange={(value) => onTournamentStyleChange(tournament, value)}
                options={styleOptions}
                style={{ width: 200 }}
                disabled={updatingTournamentId === tournament.tournamentId || !isActive}
            />
            {canAdvance && <Button type="primary" onClick={() => onAdvanceTournament(tournament)} disabled={updatingTournamentId === tournament.tournamentId} loading={advancingTournamentId === tournament.tournamentId}>{t("beerpong.tournament.details.actions.generateNextRound")}</Button>}
            {isActive && <Popconfirm title={t("beerpong.tournament.details.popconfirm.finishTitle")} description={t("beerpong.tournament.details.popconfirm.finishDescription")} onConfirm={() => onTournamentStatusChange(tournament, TournamentStatus.TOURNAMENT_STATUS_COMPLETED)}>
                <Button disabled={updatingTournamentId === tournament.tournamentId || canAdvance}>{t("beerpong.tournament.details.actions.complete")}</Button>
            </Popconfirm>}
            {tournament.status !== TournamentStatus.TOURNAMENT_STATUS_CANCELLED && <Popconfirm title={t("beerpong.tournament.details.popconfirm.cancelTitle")} description={t("beerpong.tournament.details.popconfirm.cancelDescription")} onConfirm={() => onTournamentStatusChange(tournament, TournamentStatus.TOURNAMENT_STATUS_CANCELLED)}>
                <Button danger disabled={updatingTournamentId === tournament.tournamentId}>{t("beerpong.tournament.details.actions.cancel")}</Button>
            </Popconfirm>}
            <Popconfirm title={t("beerpong.tournament.details.popconfirm.deleteTitle")} description={t("beerpong.tournament.details.popconfirm.deleteDescription")} okText={t("beerpong.tournament.details.actions.delete")} okButtonProps={{ danger: true }} onConfirm={() => onDeleteTournament(tournament)}>
                <Button danger loading={isDeletingTournament}>{t("beerpong.tournament.details.actions.delete")}</Button>
            </Popconfirm>
        </Space>
    );

    return (
        <>
            <Card
            style={{ background: "transparent", width: "100%" }}
            size={screens.xs ? "small" : "default"}
            styles={{ body: { padding: cardPadding } }}
        >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <TournamentHeader
                    name={tournament.tournamentName}
                    status={tournamentDisplayStatus}
                    format={styleLabel(tournament.style, t)}
                    kind={<Space size={4} wrap>
                        <Tag color={visibilityMeta.color}>{visibilityMeta.label}</Tag>
                        {tournament.requiresPassword && <StatusTag tone="warning">{t("beerpong.tournament.details.tags.passwordRequired")}</StatusTag>}
                        <Tag color={canManage ? "blue" : undefined}>{t(canManage ? "beerpong.tournament.details.tags.managedByYou" : "beerpong.tournament.details.tags.viewOnly")}</Tag>
                    </Space>}
                    actions={headerActions}
                    labels={{ Running: t("beerpong.tournament.details.status.active"), Completed: t("beerpong.tournament.details.status.completed"), Cancelled: t("beerpong.tournament.details.status.cancelled") }}
                />
                <Text type="secondary">{t("beerpong.tournament.details.meta.createdAt", { date: dayjs(tournament.createdAt).format("DD.MM.YYYY HH:mm") })}</Text>
                {!canManage && <Text type="secondary">{t("beerpong.tournament.details.meta.creatorOnly")}</Text>}
            {isLockedView ? (
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    <Alert
                        type="info"
                        showIcon
                        message={t("beerpong.tournament.details.alerts.passwordTitle")}
                        description={t("beerpong.tournament.details.alerts.passwordDescription")}
                    />
                    <Form
                        layout="vertical"
                        form={unlockForm}
                        onFinish={handleUnlockSubmit}
                        requiredMark={false}
                    >
                        <Form.Item
                            label={t("beerpong.tournament.details.forms.unlockPasswordLabel")}
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: t("beerpong.tournament.details.forms.unlockPasswordRule"),
                                },
                            ]}
                        >
                            <Input.Password
                                placeholder={t("beerpong.tournament.details.forms.unlockPasswordPlaceholder")}
                                autoComplete="current-password"
                                maxLength={64}
                            />
                        </Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isUnlocking}
                            block={screens.xs}
                        >
                            {t("beerpong.tournament.details.actions.unlock")}
                        </Button>
                    </Form>
                </Space>
            ) : (
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    {roundRobinOutcome && (
                        <Alert
                            type={roundRobinOutcome.pendingMatches > 0 ? "info" : "success"}
                            showIcon
                            message={t(
                                `beerpong.tournament.details.roundRobin.${
                                    roundRobinOutcome.pendingMatches > 0 ? "leaders" : "winners"
                                }`,
                                { count: roundRobinOutcome.leaders.length }
                            )}
                            description={roundRobinOutcome.leaders
                                .map((leader) => `${leader.teamName} (${leader.wins}-${leader.losses})`)
                                .join(", ")}
                        />
                    )}
                    {tournament.status === TournamentStatus.TOURNAMENT_STATUS_COMPLETED && (
                        <Alert
                            className="bp-completed-banner"
                            type="success"
                            showIcon
                            message={t("beerpong.tournament.details.match.winnerTag")}
                            description={completedWinner}
                        />
                    )}
                    {renderTeams()}
                    {canManageElimination && <div className="bp-eliminated-rounds">
                        {tournament.teams.map((team) => {
                            if (!team.isEliminated) return null;
                            const round = eliminationRoundByTeam.get(team.teamId);
                            return <StatusTag key={team.teamId} tone="error">{round
                                ? t("beerpong.tournament.details.match.eliminatedRound", { round })
                                : t("beerpong.tournament.details.match.eliminatedTag")}</StatusTag>;
                        })}
                    </div>}
                    {renderMatches()}
            </Space>
            )}
            </Space>
        </Card>
            <Modal
                title={t("beerpong.tournament.details.forms.accessModalTitle")}
                open={accessModalVisible}
                onOk={handleAccessSubmit}
                onCancel={closeAccessModal}
                okText={t("beerpong.tournament.details.forms.accessModalSave")}
                confirmLoading={updatingAccess}
                destroyOnHidden
            >
                <Form layout="vertical" form={accessForm} onFinish={handleAccessSubmit}>
                    <Form.Item
                        label={t("beerpong.tournament.details.forms.visibilityLabel")}
                        name="visibility"
                        rules={[
                            {
                                required: true,
                                message: t("beerpong.tournament.details.forms.visibilityRule"),
                            },
                        ]}
                    >
                        <Select
                            options={visibilityOptions.map(({ value, label }) => ({ value, label }))}
                        />
                    </Form.Item>
                    {modalRequiresPassword && (
                        <Form.Item
                            label={t("beerpong.tournament.details.forms.passwordLabel")}
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: t("beerpong.tournament.details.forms.passwordRule"),
                                },
                            ]}
                        >
                            <Input.Password
                                maxLength={64}
                                placeholder={t("beerpong.tournament.details.forms.passwordPlaceholder")}
                                autoComplete="new-password"
                            />
                        </Form.Item>
                    )}
                    {!modalRequiresPassword && (
                        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                            {t("beerpong.tournament.details.forms.passwordRemovalNotice")}
                        </Paragraph>
                    )}
                </Form>
            </Modal>
            <Modal
                className="bp-match-modal"
                title={selectedMatch && t("beerpong.tournament.details.matches.compactTitleWithRound", {
                    number: matchNumberMap.get(selectedMatch.matchId) ?? selectedMatch.matchId,
                    round: selectedMatch.roundNumber ?? 1,
                })}
                open={Boolean(selectedMatch)}
                onCancel={() => setSelectedMatchId(null)}
                footer={null}
                destroyOnHidden
            >
                {selectedMatch && renderMatchContent(matchRenderContext, selectedMatch, matchNumberMap, canEditMatches)}
                {slotConflict && selectedMatch?.matchId === slotConflict.match.matchId && uiTournament && <SlotConflictPrompt
                    conflict={slotConflict.conflict}
                    matches={uiTournament.matches}
                    entrants={uiTournament.entrants}
                    entrantId={slotConflict.entrantId}
                    labels={conflictLabels}
                    onSwap={(conflict) => updateSwappedTeams(tournament, slotConflict, conflict, onMatchTeamsChange, setSlotConflict)}
                    onKeepBoth={() => {
                        const payload = slotConflict.slot === "A" ? { teamAId: slotConflict.entrantId } : { teamBId: slotConflict.entrantId };
                        onMatchTeamsChange(tournament, slotConflict.match, payload);
                        setSlotConflict(null);
                    }}
                    onCancel={() => setSlotConflict(null)}
                />}
            </Modal>
        </>
    );
};

export default TournamentDetails;
